import { useCallback, useSyncExternalStore } from 'react';

/**
 * "Ring for all my facilities": an operator who covers several facilities at once (for example two
 * stores with nobody else set up there) keeps the loud ring for every one of them on this device,
 * not only for the facility selected in Hub. Kept per operator on the device, across logout and
 * session expiry (DEVICE_STORAGE_KEYS in libs/providers authSession), and followed by every tab.
 */

export const RING_ALL_KEY = 'hub-alerts-ring-all';
const listeners = new Set<() => void>();

/** The operators (ERP user ids) who chose it on this device. */
const readIds = (): Array<string> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RING_ALL_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

export const readRingAll = (userId: string | number | null | undefined) =>
  userId !== null && userId !== undefined && readIds().includes(String(userId));

export const writeRingAll = (userId: string | number, ringAll: boolean) => {
  const id = String(userId);
  const others = readIds().filter(existing => existing !== id);
  const next = ringAll ? [...others, id] : others;
  try {
    if (next.length) localStorage.setItem(RING_ALL_KEY, JSON.stringify(next));
    else localStorage.removeItem(RING_ALL_KEY);
  } catch {
    // Storage blocked: nothing to keep.
  }
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Another Hub tab changed it (or storage was cleared).
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === RING_ALL_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const useRingAll = (userId: string | number | null | undefined) => {
  const ringAll = useSyncExternalStore(
    subscribe,
    () => readRingAll(userId),
    () => false,
  );
  const setRingAll = useCallback(
    (next: boolean) => {
      if (userId !== null && userId !== undefined) writeRingAll(userId, next);
    },
    [userId],
  );
  return [ringAll, setRingAll] as const;
};
